import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { TQC2060_R01HeaderDto } from './Dto_TQC2060_R01';
import { useTQC2060_R01Store } from './Store_TQC2060_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'TQC2060_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2060_R01HeaderDto> | null>;
	defaultTQC2060_R01HeaderRow: TQC2060_R01HeaderDto;
}

export const Table_TQC2060_R01 = (props: Props) => {
	const { gridRef, defaultTQC2060_R01HeaderRow } = props;

	const {
		TQC2060_R01List,
		selectedTQC2060_R01, setSelectedTQC2060_R01, initSelectedTQC2060_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useTQC2060_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'testResultNo',
		gridData: TQC2060_R01List,
		defaultGridData: defaultTQC2060_R01HeaderRow,
		selectedRow: selectedTQC2060_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		testDt: '시험일자',
		testNm: '시험명',
		empNm: '시험자',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'testResultNo')(params), []);

	const baseColumnDefs: ColDef<TQC2060_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '시험일자', colId: 'testDt', field: 'testDt', width: 120,
			cellStyle: (p) => getCellStyle(p, 'testDt'),
		},
		{
			headerName: '시험자', colId: 'empNm', field: 'empNm', width: 120,
			cellStyle: (p) => getCellStyle(p, 'empNm'),
		},
		{
			headerName: '시험명', colId: 'testNm', field: 'testNm', width: 160,
			cellStyle: (p) => getCellStyle(p, 'testNm'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<TQC2060_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<TQC2060_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<TQC2060_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedTQC2060_R01?.testResultNo === rowData.testResultNo;
		if (isSelected) {
			setSelectedTQC2060_R01(undefined);
		} else {
			setSelectedTQC2060_R01(rowData);
		}
		setDetailScrollTop(null);
	}, [selectedTQC2060_R01, setSelectedTQC2060_R01, setDetailScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		testDt: `${TQC2060_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [TQC2060_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [TQC2060_R01List, selectedTQC2060_R01]);

	return (
		<GridContextWrapper<TQC2060_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedTQC2060_R01}
			defaultRowData={defaultTQC2060_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<TQC2060_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<TQC2060_R01HeaderDto>({
					idKey: 'testResultNo',
					selectedList: selectedTQC2060_R01 ? [selectedTQC2060_R01.testResultNo] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
